// Shared Autotask ticket-status helpers. Lived as identical copies in
// Tickets.tsx and Dashboard.tsx; lifted here so the colour mapping stays in
// sync across the list, the detail header, and the dashboard.

/**
 * Map an Autotask status label to a coloured badge class.
 *
 * Distinct colours let the common states separate at a glance (issue #24):
 * "New" and "Customer Note Added" both fell through to the violet `badge-open`
 * before, so they looked identical. Customer-action states (a note added or a
 * reply from the client — iQuest's turn) now get their own colour.
 */
export function statusBadgeClass(label: string | null | undefined): string {
  if (!label) return "badge badge-open";
  const l = label.toLowerCase();
  if (
    l.includes("complete") ||
    l.includes("closed") ||
    l.includes("resolved") ||
    l.includes("cancelled")
  ) {
    return "badge badge-closed";
  }
  // Customer has just acted (note added / responded) — distinct from the
  // untouched "New" state so the eye catches "your turn" tickets.
  if (
    l.includes("customer note") ||
    l.includes("customer respond") ||
    l.includes("note added")
  ) {
    return "badge badge-customer";
  }
  if (l.includes("waiting") || l.includes("hold")) return "badge badge-waiting";
  if (l.includes("progress")) return "badge badge-progress";
  if (l.includes("critical")) return "badge badge-critical";
  return "badge badge-open";
}

export function isClosedStatus(label: string | null | undefined): boolean {
  if (!label) return false;
  const l = label.toLowerCase();
  return (
    l.includes("complete") ||
    l.includes("closed") ||
    l.includes("resolved") ||
    l.includes("cancelled")
  );
}

/** Where a ticket stands on its first response, as an inline pill.
 *
 * The value is computed on the SERVER (services/sla_posture.py) and arrives on
 * each list row as `sla_posture`. It used to be worked out here, from a flat
 * four wall-clock hours, and once the Response times page began counting
 * working time the two disagreed: the same ticket could read "On track" in the
 * list and "At risk" on the page whose job it is to say so (issue #102).
 *
 * A list is about what HAPPENED, so an answered ticket settles to met or
 * breached and stops moving. Tickets still awaiting an answer use exactly the
 * rule the Response times page uses, which is the point.
 */
export type RowSlaPosture =
  | "breached"
  | "at_risk"
  | "on_track"
  | "met"
  | "missed"
  | "no_target";

export const SLA_PILL: Record<RowSlaPosture, { label: string; cls: string }> = {
  // Still unanswered past its deadline: the clock is running.
  breached: { label: "Breached", cls: "badge badge-critical" },
  at_risk: { label: "At risk", cls: "badge badge-waiting" },
  on_track: { label: "On track", cls: "badge badge-progress" },
  met: { label: "SLA met", cls: "badge badge-progress" },
  // Answered, but late. Over rather than running, and named the same here as on
  // the SLA page so one ticket does not read two ways.
  missed: { label: "SLA missed", cls: "badge badge-critical" },
  no_target: { label: "No SLA target", cls: "badge badge-closed" },
};

/** The pill for a row, or null when the server sent no posture — or one this
 *  build has no entry for, which is what a browser on an older bundle than the
 *  API receives. Without the fallback the lookup returns undefined in defiance
 *  of the declared type and the caller renders an empty pill (issue #106). */
export function slaPill(
  posture: RowSlaPosture | null | undefined,
): { label: string; cls: string } | null {
  if (!posture) return null;
  const pill: { label: string; cls: string } | undefined = SLA_PILL[posture];
  return pill ?? null;
}
